import CurriculumDownloadModal from '@components/home/curriculum-download-modal'
import { Trans, defineMessage } from '@lingui/macro'
import { useState } from 'react'
import CourseCard from '@components/locale-pages/home/course-card'

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-pink hover:border-pink',
]

const COURSE_PAGE_URL = {
  codingGrandMaster: '/courses/coding-grandmaster/',
  codingProdigy: '/courses/coding-prodigy/',
  codingchampion: '/courses/coding-champion/',
  scratchPrograming: '/courses/scratch-programming/',
  pythonForKids: '/courses/python-for-kids/',
  gameDevelopment: '/courses/game-development/',
  webDevelopment: 'https://www.codingal.com/courses/web-development/',
  appDevelopment: 'https://www.codingal.com/courses/app-development/',
  dataScience: 'https://www.codingal.com/courses/data-science/',
  roblox: 'https://www.codingal.com/courses/roblox-coding/',
  APComputerScienceA: 'https://www.codingal.com/courses/ap-computer-science-a/',
  IoiAlgorithms: 'https://www.codingal.com/courses/ioi-algorithms-course/',
  aiAndMl: 'https://www.codingal.com/courses/ai-and-machine-learning/',
}

export type CourseInfoType = {
  title: JSX.Element
  titleCompactView: JSX.Element
  grade: string
  activites: number
  lessons: number
  duration: string
  Age: string
  description: any
  image: string
  altText: string
  outcomes: any[]
  courseCode: string
  curriculumPDFLink: string
  trialCourseQuery: string
  isBestSeller: boolean
  coursePageUrl: string
  gradeOptions?: number[]
  isAICourse?: boolean
  prerequisite?: string
}
export type CourseVideoType = {
  title: string
  url?: string
  image: string
  altText: string
}

export type CourseFeaturesType = {
  outcomes: string[]
  skills: {
    imagePath: string
    skillName: string
  }[]
}

export type CourseLearningType = {
  title: string
  description: JSX.Element
  image: string
}

export const COURSE_LEARNING: Record<string, CourseLearningType[]> = {
  THUNKABLE: [
    {
      title: 'Real-World Development Tools',
      image: 'coding-tools',
      description: (
        <>
          In our Application Development for Kids course, students use Thunkable
          to build practical apps like a PDF Reader, Space Warriors game, and a
          Check-List App. These projects introduce key components like buttons,
          images, and logic blocks, laying a strong foundation for creating
          interactive apps.
        </>
      ),
    },
    {
      title: 'Creative and Fun Projects',
      image: 'design-thinking',
      description: (
        <>
          As they progress, students work on more advanced projects such as Mole
          Mash, a Weather App, and a Barcode Scanner App, learning about
          animation, APIs, and sensor integration.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          Students integrate AI features like speech recognition and
          text-to-speech, making their apps smarter and more interactive.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          By the end of the course, students develop essential coding skills,
          including working with APIs, sensors, and AI, preparing them for
          future app development.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Our online platform offers flexible class scheduling, gamified
          learning with quizzes, projects, and certifications, making learning
          fun and motivating.
        </>
      ),
    },
  ],
  JAVA: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Your child will gain hands-on experience with industry-standard tools
          like Java, Visual Studio Code, Git, and GitHub. These tools not only
          teach the basics of programming but also prepare students for
          real-world challenges in technology. By mastering them, your child
          will develop critical skills that align with modern tech environments,
          giving them a head start in their technology journey.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Our curriculum features a range of creative projects designed to make
          learning Java engaging and meaningful. Students solve coding problems
          and master algorithms to tackle complex tasks. Each project encourages
          creativity while reinforcing key programming concepts, ensuring that
          learning is both enjoyable and effective.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          We provide a dynamic learning experience through engaging activities,
          assignments, and quizzes. Our interactive approach keeps learners
          motivated by allowing them to apply knowledge right away. Tasks
          gradually increase in difficulty, ensuring continuous learning while
          reinforcing previously taught concepts.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Through coding, students develop strong problem-solving and
          critical-thinking skills, essential for success in programming and
          beyond. By working on challenging coding problems and collaborative
          projects, your child will learn how to analyze situations, develop
          logical solutions, and think creatively.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning with Codingal is flexible and convenient. Class recordings
          and detailed lesson plans are available so kids can access materials
          anytime. This flexibility ensures that learning fits seamlessly into
          their schedule, allowing them to progress at their own pace with
          expert guidance and a supportive community.
        </>
      ),
    },
  ],
  TRINKET: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Codingal uses tools like the Trinket platform and Python, which are
          real-world programming languages. Kids get to work with the same tools
          used by professional coders, making them feel like real-life
          programmers as they create games, animations, and projects.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Students get to explore their creativity through projects like drawing
          with Turtle graphics, building games, and even creating their own
          animations. These projects are designed to be fun, allowing kids to
          express themselves while learning essential coding skills.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          With interactive lessons, kids receive immediate feedback on their
          code. This helps them learn quickly and make real-time corrections,
          making the learning process more engaging and effective. Plus,
          Codingal uses a project-based approach, so kids can track their
          progress and create something cool at every step.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          The curriculum encourages kids to think critically and solve
          problems—essential skills in coding. By working through challenges and
          puzzles, kids learn how to break down problems and find creative
          solutions, skills that are crucial both in coding and in real-world
          scenarios.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Our online coding classes makes it easy for kids to learn coding with
          flexible class rescheduling options. Our student dashboard offers a
          gamified learning experience with features like projects, quizzes,
          certifications, student leaderboard and more.
        </>
      ),
    },
  ],
  PYGAME: [
    {
      title: 'Real-World Coding Tools',
      image: 'coding-tools',
      description: (
        <>
          Codingal uses tools like Pygame and Python, giving kids hands-on
          experience with real programming languages. They work with
          professional tools, creating games, animations, and projects like
          real-life programmers.
        </>
      ),
    },
    {
      title: 'Creative Projects',
      image: 'design-thinking',
      description: (
        <>
          Kids explore creativity by building games, animations, and using
          Turtle graphics. These fun projects help them develop key coding
          skills.
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          With interactive lessons and real-time feedback, kids quickly learn
          and make corrections, staying engaged through project-based learning.
        </>
      ),
    },
    {
      title: 'Focus on Problem-Solving and Critical Thinking',
      image: 'logical-thinking',
      description: (
        <>
          Our curriculum encourages critical thinking and problem-solving,
          helping kids break down challenges and find creative solutions, both
          in coding and real life.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Codingal’s online platform allows kids to learn from anywhere, with an
          easy setup that lets them focus on learning and having fun.
        </>
      ),
    },
    {
      title: 'Learning with Real-World Concepts',
      image: 'real-world-concepts',
      description: (
        <>
          We connect classroom lessons with real-life scenarios, using analogies
          to make complex concepts easier to understand. For example, think of
          the game window as a stage, sprites as actors, and collision detection
          like bumper cars. These relatable examples help students grasp coding
          concepts more naturally.
        </>
      ),
    },
  ],
  ANIMATION: [
    {
      title: 'Hands-On Experience with Industry Tools',
      image: 'coding-tools',
      description: (
        <>
          Students gain hands-on experience with tools and platforms like
          Scratch, Code.org, Python with Pygame, Web Development (HTML, CSS,
          JavaScript), and Roblox Studio. These platforms not only make learning
          animation accessible but also prepare kids for real-world applications
          in technology. By working with these tools, students develop coding
          expertise while bringing their creative ideas to life.
        </>
      ),
    },
    {
      title: 'Creative and Fun Animation Projects',
      image: 'design-thinking',
      description: (
        <>
          Our curriculum is packed with imaginative projects designed to make
          learning animation engaging and enjoyable. Kids will create
          interactive stories, animated games, dynamic websites, and even 3D
          experiences in Roblox. Every project encourages kids to think
          creatively, apply their coding knowledge, and master animation
          concepts while having a blast!
        </>
      ),
    },
    {
      title: 'Interactive Learning',
      image: 'problem-solving-2',
      description: (
        <>
          At Codingal, we make learning dynamic and interactive. Through
          hands-on activities, coding challenges, and quizzes, students actively
          apply what they learn and watch their animations come to life. Our
          curriculum is designed to gradually introduce new concepts, helping
          students build confidence and coding skills step by step.
        </>
      ),
    },
    {
      title: 'Focus on Creativity and Problem-Solving',
      image: 'logical-thinking',
      description: (
        <>
          Animation at Codingal is all about creativity and problem-solving.
          Students not only learn to code but also design their animations with
          logic and fun solutions. They’ll develop critical thinking skills as
          they analyze challenges, create unique animations, and bring their
          ideas to life in a way that’s both interactive and visually exciting.
        </>
      ),
    },
    {
      title: 'Easy Accessibility',
      image: 'easy-accessibility',
      description: (
        <>
          Learning animation with Codingal is flexible and convenient. With
          class recordings, detailed lesson plans, and 24/7 access to resources,
          students can learn at their own pace and revisit concepts whenever
          they need. This flexibility ensures kids stay motivated and continue
          progressing on their creative animation journey.
        </>
      ),
    },
  ],
  TURTLE: [
    {
      title: 'Hands-On Experience with Python Turtle Graphics',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students will gain hands-on experience with Python Turtle, a powerful
          tool used by developers to create interactive graphics and animations.
          With Turtle, kids get a real-world introduction to programming and
          problem-solving while building projects that sharpen their creativity.
          This lays a solid foundation for both coding and AI education.
        </>
      ),
    },
    {
      title: 'Exciting Turtle Graphics Projects',
      image: 'laptop-coding',
      description: (
        <>
          From drawing shapes to creating geometric patterns and interactive
          designs, our curriculum is packed with exciting, hands-on projects.
          Students will work on real-world challenges like fractals, spirals,
          and interactive drawings while exploring the features of the Turtle
          module.
        </>
      ),
    },
    {
      title: 'Interactive and Progressive Learning',
      image: 'analytical-thinking',
      description: (
        <>
          The Turtle curriculum is structured to guide students from basic
          Python concepts to advanced graphics. They will work through
          interactive lessons, fun challenges, and coding sessions, gradually
          building confidence in coding and design as they progress.
        </>
      ),
    },
    {
      title: 'Flexibility and Accessibility',
      image: 'setting-arrow',
      description: (
        <>
          With live online classes, session recordings, and full-time mentor
          support, students can learn React at their own pace. Whether
          they&lsquo;re revisiting concepts or tackling new projects, the course
          is designed to accommodate any schedule and learning style.
        </>
      ),
    },
    {
      title: 'Focus on Creativity and Problem-Solving',
      image: 'logical-thinking',
      description: (
        <>
          Students are encouraged to think creatively—designing shapes,
          patterns, and animations using Turtle graphics. By creating real
          projects, kids not only learn how to code but also develop essential
          problem-solving and design thinking skills.
        </>
      ),
    },
  ],

  MIT_APP_INVENTOR: [
    {
      title: 'Hands-On Experience with Industry Tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Students get hands-on experience using MIT App Inventor—an intuitive,
          block-based development platform designed by MIT. With this tool, kids
          learn to build real Android mobile apps while developing a strong
          understanding of coding logic and mobile design.
        </>
      ),
    },
    {
      title: 'Exciting App Development Projects',
      image: 'laptop-coding',
      description: (
        <>
          The curriculum is packed with exciting projects like quiz apps,
          calculators, games, and even health trackers. These fun, creative
          challenges keep kids engaged as they learn how to design, code, and
          publish fully functional mobile apps using AI & coding principles.
        </>
      ),
    },
    {
      title: 'Interactive and Progressive Learning',
      image: 'analytical-thinking',
      description: (
        <>
          Learning with MIT App Inventor is highly visual and interactive.
          Students build apps through guided steps, gradually increasing the
          complexity of their creations. Concepts are introduced progressively
          to ensure students grow in confidence and skill with each lesson.
        </>
      ),
    },
    {
      title: 'Flexibility and Accessibility',
      image: 'setting-arrow',
      description: (
        <>
          Codingal&lsquo;s MIT App Inventor course offers 24/7 access to
          recorded lessons, live class support, and step-by-step resources.
          Whether your child is a beginner or advancing fast, the course adapts
          to their learning pace—anytime, anywhere.
        </>
      ),
    },
    {
      title: 'Focus on Creativity and Problem-Solving',
      image: 'logical-thinking',
      description: (
        <>
          Through real-world app creation, kids learn to break down user needs,
          design functional interfaces, and debug code. These activities boost
          creative thinking, critical analysis, and problem-solving—essential
          skills for both coding and future careers.
        </>
      ),
    },
  ],

  BOOTSTRAP: [
    {
      title: 'Hands-On Experience with Industry Tools',
      image: 'animation-and-motion-control',
      description: (
        <>
          Kids learn to use HTML, CSS, and Bootstrap—one of the most popular
          front-end frameworks in web development. These tools help students
          build responsive websites and gain real-world exposure to how modern
          web pages are created.
        </>
      ),
    },
    {
      title: 'Exciting Bootstrap Projects',
      image: 'laptop-coding',
      description: (
        <>
          Our Bootstrap course is built to gradually introduce students to
          advanced UI concepts. Through live instruction, code-along sessions,
          and interactive quizzes, students master each topic before moving to
          the next.
        </>
      ),
    },
    {
      title: 'Interactive and Progressive Learning',
      image: 'analytical-thinking',
      description: (
        <>
          Kids not only learn how websites are built—they design them from
          scratch. Through creative layouts, theme selection, and responsive
          adjustments, they build their own websites while improving their
          ability to think critically and solve design problems.
        </>
      ),
    },
    {
      title: 'Flexibility and Accessibility',
      image: 'setting-arrow',
      description: (
        <>
          All students get access to flexible scheduling, session recordings,
          and self-paced materials. Whether it&lsquo;s a weekend catch-up or
          late-night revision, learning Bootstrap at Codingal fits around your
          child&lsquo;s routine.
        </>
      ),
    },
  ],
}

export const COURSE_FEATURES: Record<string, CourseFeaturesType> = {
  AI_AND_ML: {
    outcomes: [
      'ChatGPT Integration',
      'Google Gemini Integration',
      'Natural Language Processing',
      'Machine Learning',
      'Image & Speech Recognition',
      'Prompt Engineering',
      'Computer Vision (Object, Face & Gesture Detection)',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/ai-ml-core-principles',
        skillName: 'AI & ML Core Principles',
      },
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Analytical Thinking',
      },
      {
        imagePath: '/images/courses/icons/logic-building',
        skillName: 'Logic Building',
      },
      {
        imagePath: '/images/courses/icons/pictoblox-edublocks',
        skillName: 'Pictoblox, EduBlocks',
      },
      {
        imagePath: '/images/courses/icons/google-gemini-hugging-face-for-llms',
        skillName: 'Google Gemini, Hugging Face for LLMs',
      },
      {
        imagePath: '/images/courses/icons/ai-ml-programming',
        skillName: 'AI, ML Programming',
      },
    ],
  },
  THUNKABLE: {
    outcomes: [
      'Learn programming basics',
      'Design appealing UIs',
      'Create multi–screen apps',
      'Build games with accelerometers',
      'Add AI for advanced features',
      'Work with real–time databases',
      'Use location sensor to find position',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/app-architecture',
        skillName: 'App Architecture',
      },
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Creative Thinking',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Design Thinking',
      },
      {
        imagePath: '/images/courses/icons/data-management',
        skillName: 'Data Management',
      },
      {
        imagePath: '/images/courses/icons/api-integration',
        skillName: 'API Integration',
      },
      {
        imagePath: '/images/courses/icons/ai-ml-programming',
        skillName: 'AI Integration & Problem-Solving',
      },
    ],
  },
  JAVA: {
    outcomes: [
      'Java basics & setup',
      'Data types, operators, control flow',
      'Loops & user input',
      'Object-oriented programming (OOP)',
      'Arrays & ArrayLists',
      'Sorting, searching, recursion',
      'File handling',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/java-foundation',
        skillName: 'Foundation in Java programming',
      },
      {
        imagePath: '/images/courses/icons/complex-problem-solving',
        skillName: 'Solve complex coding challenges',
      },
      {
        imagePath: '/images/courses/icons/implement-algorithms',
        skillName: 'Implement Algorithms efficiently',
      },
      {
        imagePath: '/images/courses/icons/master-oops',
        skillName: 'Master key OOP principles',
      },
    ],
  },
  TRINKET: {
    outcomes: [
      'Build different shapes and patterns',
      'Draw with the Turtle library',
      'Explore the Trinket module',
      'Make a fun caterpillar using Python code',
      'Master typing code',
      'Understand loops and conditionals',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/python-programming',
        skillName: 'Python Programming',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Creative Coding',
      },
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Problem Solving',
      },
      {
        imagePath: '/images/courses/icons/typing-speed',
        skillName: 'Typing Speed',
      },
      {
        imagePath: '/images/courses/icons/api-integration',
        skillName: 'Logical Thinking',
      },
    ],
  },
  PYGAME: {
    outcomes: [
      'Install & Configure Pygame',
      'Game Building Basics',
      'Add Images & Titles',
      'Animate with Sprites',
      'Build Space Invaders',
      'Collision Detection & Responses',
      'Handle Game Events',
      'Design & Manage Game Window',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/problem-solving',
        skillName: 'Problem solving',
      },
      {
        imagePath: '/images/courses/icons/logical-thinking',
        skillName: 'Logical thinking',
      },
      {
        imagePath: '/images/courses/icons/python-programming',
        skillName: 'Game development with Python',
      },
      {
        imagePath: '/images/courses/icons/design-and-animate-creatively',
        skillName: 'Design & animate creatively',
      },
      {
        imagePath: '/images/courses/icons/creating-dynamic-game-environments',
        skillName: 'Creating Dynamic Game Environments',
      },
      {
        imagePath: '/images/courses/icons/mastering-event-handling',
        skillName: 'Mastering Event Handling',
      },
    ],
  },
  ANIMATION: {
    outcomes: [
      'Cross-Platform Skills',
      'Motion and Effects',
      'Platform-Specific Tools and Techniques',
      'Sound Integration',
      'Sprite and Object Manipulation',
      'User Interaction',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Creative Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/logical-reasoning',
        skillName: 'Logical Reasoning and Sequencing',
      },
      {
        imagePath: '/images/courses/icons/design-thinking',
        skillName: 'Storytelling and Visual Design Skills',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Animation and Motion Control Techniques',
      },
      {
        imagePath: '/images/courses/icons/user-interaction-and-event-handling',
        skillName: 'User Interaction and Event Handling',
      },
      {
        imagePath: '/images/courses/icons/debugging-troubleshooting',
        skillName: 'Debugging and Troubleshooting Proficiency',
      },
    ],
  },
  TURTLE: {
    outcomes: [
      'Introduction to Python programming',
      'Program graphics and animations with Canvas & Turtle libraries',
      'Using loops and functions in coding',
      'Create geometric patterns and designs',
      'Creating recursive patterns with Turtle',
      "Work with Python's Turtle library",
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Designing Interactive Drawings and Graphics',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Understanding of Basic Algorithms',
      },
      {
        imagePath: '/images/courses/icons/node-tapping',
        skillName: 'Logical Thinking and Problem-Solving',
      },
      {
        imagePath: '/images/courses/icons/design-and-animate-creatively',
        skillName: 'Creativity in Designing Projects',
      },
    ],
  },

  MIT_APP_INVENTOR: {
    outcomes: [
      'Basics of mobile app development',
      'How to use MIT App Inventor’s drag-and-drop blocks',
      'Creating app screens, buttons, forms, and actions',
      'Designing interactive user interfaces',
      'Introductory AI features through App Inventor extensions',
      'Develop design and problem-solving skills',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Logic-Based Thinking',
      },
      {
        imagePath: '/images/courses/icons/mobile-app',
        skillName: 'Mobile UI/UX Design',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Event-Driven Programming',
      },
      {
        imagePath: '/images/courses/icons/debugging',
        skillName: 'Debugging and Testing',
      },
      {
        imagePath: '/images/courses/icons/ai-and-coding',
        skillName: 'AI & Coding Foundations',
      },
      {
        imagePath: '/images/courses/icons/android-app',
        skillName: 'Create Fully Functional Android Apps',
      },
    ],
  },

  BOOTSTRAP: {
    outcomes: [
      'Fundamentals of HTML and CSS',
      'How to use Bootstrap to build websites',
      'Structure pages with layout grids',
      'Customize pre-built Bootstrap components',
      'Create interactive forms and buttons',
      'Test and optimize website performance across devices',
    ],
    skills: [
      {
        imagePath: '/images/courses/icons/analytical-thinking',
        skillName: 'Web Design and Layout Fundamentals',
      },
      {
        imagePath: '/images/courses/icons/complex-thinking',
        skillName: 'Problem-Solving Using Real Coding Tools',
      },
      {
        imagePath: '/images/courses/icons/learning',
        skillName: 'A Strong Foundation in Website Development',
      },
      {
        imagePath: '/images/courses/icons/coding',
        skillName: 'Early Exposure to Front-End Web Development',
      },
      {
        imagePath: '/images/courses/icons/animation-and-motion-control',
        skillName: 'Confidence in Using Professional Tools',
      },
      {
        imagePath: '/images/courses/icons/creativity',
        skillName: 'Visual Creativity and Attention to Detail',
      },
    ],
  },
}

export const COURSE_INFO: Record<string, CourseInfoType> = {
  cgm: {
    title: <Trans>AI & Coding Grandmaster: Master Coding Skills</Trans>,
    titleCompactView: (
      <Trans>AI & Coding Grandmaster: Master Coding Skills</Trans>
    ),
    grade: '1-10',
    activites: 350,
    lessons: 144,
    duration: '12-18',
    Age: '5-15',
    description: defineMessage({
      message: `Explore 144 interactive lessons to become a master of AI and coding. Choose a guided path or personalize your journey.`,
    }),
    image: 'coding-grandmaster',
    altText: 'Online Coding Course for Kids & Teens to Master Coding Skills',
    outcomes: [
      defineMessage({ message: 'Build apps, games, and websites' }),
      defineMessage({ message: 'Master AI and coding skills' }),
      defineMessage({ message: 'Engage in project-based learning' }),
      defineMessage({
        message:
          'Earn an AI & Coding Grandmaster certificate, accredited by STEM.org',
      }),
    ],
    courseCode: 'cgm',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: true,
    coursePageUrl: COURSE_PAGE_URL.codingGrandMaster,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    isAICourse: true,
  },
  cp: {
    title: <Trans>AI & Coding Prodigy: Code Like a Pro</Trans>,
    titleCompactView: <Trans>AI & Coding Prodigy: Code Like a Pro</Trans>,
    grade: '1-12',
    activites: 184,
    lessons: 96,
    duration: '9-12',
    Age: '5-17',
    description: defineMessage({
      message:
        'A 96-lesson curriculum for kids and teens to master AI, coding, and real-world applications through projects.',
    }),
    image: 'coding-prodigy',
    altText: 'Online Coding Course for Kids & Teens to Code Like a Pro',
    outcomes: [
      defineMessage({ message: 'Learn to code like a pro' }),
      defineMessage({ message: 'Create amazing apps and games' }),
      defineMessage({
        message:
          'Earn an AI & Coding Prodigy certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Master problem-solving skills' }),
    ],
    courseCode: 'cp',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingProdigy,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    isAICourse: true,
  },
  cc: {
    title: <Trans>AI & Coding Champion: Learn to Code</Trans>,
    titleCompactView: <Trans>AI & Coding Champion: Learn to Code</Trans>,
    grade: '1-12',
    activites: 50,
    lessons: 48,
    duration: '4-6',
    Age: '5-17',
    description: defineMessage({
      message:
        'Kickstart AI & coding for kids and teens with 48 fun lessons and hands-on projects that build skills and creativity.',
    }),
    image: 'coding-champion',
    altText: 'Coding Course for Kids & Teens to Learn to Code',
    outcomes: [
      defineMessage({ message: 'Kickstart your AI & coding journey' }),
      defineMessage({ message: 'Build amazing apps, games, & websites' }),
      defineMessage({
        message:
          'Earn an AI & Coding Champion Certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Unlock problem-solving superpowers' }),
    ],
    courseCode: 'cc',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingchampion,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    isAICourse: true,
  },
  sp: {
    title: <Trans>Scratch Programming with AI: Unleash Your Creativity</Trans>,
    titleCompactView: (
      <Trans>Scratch Programming with AI: Unleash Your Creativity</Trans>
    ),
    grade: '1-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '5-14',
    description: defineMessage({
      message:
        'Master AI and coding with Scratch: create games, animations, explore AI, and unlock endless creative potential.',
    }),
    image: 'scratch-programming',
    altText: 'Online Scratch Programming Course for Kids & Teens',
    outcomes: [
      defineMessage({ message: 'Learn block-based coding' }),
      defineMessage({ message: 'Create fun animations & games' }),
      defineMessage({
        message: 'Explore the basics of Artificial Intelligence',
      }),
      defineMessage({ message: 'Design interactive stories & digital art' }),
    ],
    courseCode: 'sp',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-scratch-curriculum.pdf',
    trialCourseQuery: 'scratch-programming-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.scratchPrograming,
    isAICourse: true,
  },
  gd: {
    title: <Trans>Game Development for Kids: Build Fun Games with AI</Trans>,
    titleCompactView: (
      <Trans>Game Development for Kids: Build Fun Games with AI</Trans>
    ),
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Boost your AI and game development skills with Scratch and Code.org—create epic games and code like a pro!',
    }),
    image: 'game-development',
    altText: 'Online Game Development Course for Kids to Build Fun Games',
    outcomes: [
      defineMessage({
        message: 'Master AI & Coding Skills for Game Development',
      }),
      defineMessage({
        message: 'Apply Skills to Real-World Projects',
      }),
      defineMessage({ message: 'Think Like a Gamer, Code Like a Pro' }),
      defineMessage({
        message: 'Animate Characters and Objects to Bring Your Games to Life',
      }),
    ],
    courseCode: 'gd',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/game-development.pdf',
    trialCourseQuery: 'game-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.gameDevelopment,
    isAICourse: true,
  },
  ad: {
    title: <Trans>App Development for Kids: Create AI-Powered Apps</Trans>,
    titleCompactView: (
      <Trans>App Development for Kids: Create AI-Powered Apps</Trans>
    ),
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Dive into AI app development—where creativity meets tech to turn imagination into intelligent apps!',
    }),
    image: 'app-development',
    altText: 'Online App Development Course for Kids to Create AI-Powered Apps',
    outcomes: [
      defineMessage({ message: 'Build interactive interfaces' }),
      defineMessage({ message: 'Design awesome apps' }),
      defineMessage({
        message:
          'Make your app super smart (integrate camera, accelerometer, GPS, etc.)',
      }),
      defineMessage({ message: 'Share your app with the world' }),
    ],
    courseCode: 'ad',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/app-development.pdf',
    trialCourseQuery: 'app-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.appDevelopment,
    isAICourse: true,
  },
  rc: {
    title: <Trans>Roblox Champion: Build & Design Epic Games</Trans>,
    titleCompactView: <Trans>Roblox Champion: Build & Design Epic Games</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Create and launch Roblox games with expert tutors. Turn your ideas into exciting games!',
    }),
    image: 'roblox-champion',
    altText: 'Online Roblox Course for Kids to Build & Design Epic Games',
    outcomes: [
      defineMessage({ message: 'Become a Roblox game builder' }),
      defineMessage({ message: 'Learn scripting with Lua' }),
      defineMessage({
        message:
          'Design fun & unique 3D games (tycoon simulations, racing games, horror experiences)',
      }),
      defineMessage({ message: 'Share your games with the world' }),
    ],
    courseCode: 'rc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/roblox-curriculum.pdf',
    trialCourseQuery: 'roblox-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.roblox,
  },
  wd: {
    title: <Trans>Website Development: Build AI-Powered Websites</Trans>,
    titleCompactView: (
      <Trans>Website Development: Build AI-Powered Websites</Trans>
    ),
    grade: '8-12',
    activites: 90,
    lessons: 90,
    duration: '10-12',
    Age: '13-17',
    description: defineMessage({
      message:
        'Learn the fundamentals of the web and enhance your skills in building Interactive web pages using HTML, CSS, JavaScript, and more.',
    }),
    image: 'web-development',
    altText:
      'Website Development Course for Teens to Learn to Build and deploy Websites',
    outcomes: [
      defineMessage({ message: 'Build stunning, responsive websites' }),
      defineMessage({ message: 'Create interactive web pages' }),
      defineMessage({ message: 'Master HTML, CSS, and JavaScript' }),
      defineMessage({
        message: 'Implement best practices in website development',
      }),
    ],
    courseCode: 'wd',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/web-development.pdf',
    trialCourseQuery: 'web-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.webDevelopment,
  },
  pc: {
    title: <Trans>Python Champion: Enhance Your Skills</Trans>,
    titleCompactView: <Trans>Python Champion: Enhance Your Skills</Trans>,
    grade: '6-12',
    activites: 90,
    lessons: 44,
    duration: '4-6',
    Age: '11-17',
    description: defineMessage({
      message:
        'Dive into Python with hands-on projects. Explore the basics, build games, design apps, and unlock advanced libraries, OOP, and more!',
    }),
    image: 'python-champion',
    altText: 'Online Python Course for Teens to Enhance Coding Skills',
    outcomes: [
      defineMessage({ message: 'Master Python basics' }),
      defineMessage({ message: 'Build awesome interactive games' }),
      defineMessage({ message: 'Create cool GUI apps' }),
      defineMessage({ message: 'Learn object-oriented programming (OOP)' }),
    ],
    courseCode: 'pc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-python-curriculum.pdf',
    trialCourseQuery: 'python-for-kids-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.pythonForKids,
  },
  ds: {
    title: <Trans>AI & Data Science for Teens: Learn Python & Analytics</Trans>,
    titleCompactView: (
      <Trans>AI & Data Science for Teens: Learn Python & Analytics</Trans>
    ),
    grade: '9-12',
    activites: 200,
    lessons: 168,
    duration: '18-20',
    Age: '13-17',
    description: defineMessage({
      message:
        'Master data science principles and gain practical experience through hands-on projects.',
    }),
    image: 'data-science',
    altText: 'Data Science Course for Teens with Python & Analytics Skills',
    outcomes: [
      defineMessage({ message: 'Master advanced Python libraries' }),
      defineMessage({ message: 'Learn data structures and algorithms' }),
      defineMessage({ message: 'Analyze and interpret complex datasets' }),
      defineMessage({
        message: 'Build machine learning and deep learning projects',
      }),
    ],
    courseCode: 'ds',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/data-science.pdf',
    trialCourseQuery: 'data-science-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.dataScience,
    isAICourse: true,
  },
  ioi: {
    title: <Trans>IOI Algorithms: Master Algorithms with Python</Trans>,
    titleCompactView: (
      <Trans>IOI Algorithms: Master Algorithms with Python</Trans>
    ),
    grade: '9-12',
    activites: 150,
    lessons: 120,
    duration: '12-14',
    Age: '13-17',
    description: defineMessage({
      message:
        'Develop advanced skills in data structures and algorithms through hands-on problem-solving.',
    }),
    image: 'ioi-algorithms',
    altText: 'IOI Algorithms Course for Teens to Master Algorithms with Python',
    outcomes: [
      defineMessage({ message: 'Master data structures and algorithms' }),
      defineMessage({ message: 'Sharpen problem-solving skills' }),
      defineMessage({ message: 'Enhance Python programming skills' }),
      defineMessage({ message: 'Prepare for coding competitions' }),
    ],
    courseCode: 'ioi',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ioi-algorithms.pdf',
    trialCourseQuery: 'algorithms',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.IoiAlgorithms,
    prerequisite: 'Basic Python & Data Structures (Grades 9 - 12)',
  },
  apcsa: {
    title: <Trans>AP Computer Science A: Programming with Java</Trans>,
    titleCompactView: (
      <Trans>AP Computer Science A: Programming with Java</Trans>
    ),
    grade: '11-12',
    activites: 70,
    lessons: 48,
    duration: '4-6',
    Age: '15-17',
    description: defineMessage({
      message:
        'Master Java programming, OOP, data structures, and algorithms through hands-on problem-solving.',
    }),
    image: 'ap-computer-science-a',
    altText:
      'AP Computer Science A Course for Teens to Learn Programming with Java',
    outcomes: [
      defineMessage({ message: 'Master Java programming' }),
      defineMessage({ message: 'Understand & apply OOP concepts' }),
      defineMessage({ message: 'Enhance algorithmic thinking' }),
      defineMessage({ message: 'Develop advanced problem-solving skills' }),
    ],
    courseCode: 'apcsa',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ap-computer-science-a.pdf',
    trialCourseQuery: 'ap-computer-science-a-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.APComputerScienceA,
  },
  aigc: {
    title: <Trans>AI Genius: ChatGPT, AI-powered Apps & Games</Trans>,
    titleCompactView: (
      <Trans>AI Genius: ChatGPT, AI-powered Apps & Games</Trans>
    ),
    grade: '3-7',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '7-14',
    description: defineMessage({
      message:
        'In this 48-lesson course, kids dive into AI, creating art, chatbots, and games, learning image, sound, pose recognition, and a bit of ChatGPT magic!',
    }),
    image: 'ai-genius',
    altText:
      'AI Genius - ChatGPT, AI-powered Apps & Games for Kids to Learn AI & Programming',
    outcomes: [
      defineMessage({ message: 'Create AI Games and Apps' }),
      defineMessage({ message: 'Build Chatbots and AI Models' }),
      defineMessage({ message: 'Train AI to Recognize and Respond' }),
      defineMessage({ message: 'Master AI Concepts Through Projects' }),
    ],
    courseCode: 'aigc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-genius.pdf',
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
    isAICourse: true,
  },
  aiepc: {
    title: <Trans>AI Expert - Hands-On AI, LLMs & Python</Trans>,
    titleCompactView: <Trans>AI Expert - Hands-On AI, LLMs & Python</Trans>,
    grade: '8-12',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '12-17',
    description: defineMessage({
      message:
        'In this 48-lesson advanced AI & ML course, students use LLMs and APIs to create projects like chatbots, translators, and image recognition tools.',
    }),
    image: 'ai-expert',
    altText:
      'AI Expert - Hands-On AI, LLMs & Python for Teens to Learn AI & Coding',
    outcomes: [
      defineMessage({ message: 'Build AI applications with APIs' }),
      defineMessage({ message: 'Develop AI-powered tools and assistants' }),
      defineMessage({
        message: 'Master AI techniques like NLP and computer vision',
      }),
      defineMessage({ message: 'Create and deploy real-time AI models' }),
    ],
    courseCode: 'aiepc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-expert.pdf',
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
    isAICourse: true,
  },
}
// TODO: Add Course Video
export const COURSE_VIDEO: Record<string, CourseVideoType[]> = {
  AI_AND_ML: [
    {
      title: 'AI Genius',
      url:
        'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI-Genius.mp4',
      image: 'ai-genius-video-thumbnail',
      altText: 'AI Genius course video thumbnail for kids',
    },
    {
      title: 'AI Expert',
      url:
        'https://codingal.s3.ap-south-1.amazonaws.com/web/videos/AI-Expert1.mp4',
      image: 'ai-expert-video-thumbnail',
      altText: 'AI Expert course video thumbnail for kids & teens',
    },
  ],
  // THUNKABLE: [
  //   {
  //     title: 'What is Thunkable?',
  //     image: 'what-is-thunkable-video-thumbnail',
  //     altText: 'What is Thunkable course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Thunkable Curriculum!',
  //     image: 'thunkable-curriculum-video-thumbnail',
  //     altText: 'Thunkable Curriculum course video thumbnail for kids',
  //   },
  // ],
  // JAVA: [
  //   {
  //     title: 'What is Java?',
  //     image: 'what-is-java-video-thumbnail',
  //     altText: 'What is Java course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Java Curriculum!',
  //     image: 'java-curriculum-video-thumbnail',
  //     altText: 'Java Curriculum course video thumbnail for kids',
  //   },
  // ],
  // TRINKET: [
  //   {
  //     title: 'What is Trinket?',
  //     image: 'what-is-trinket-video-thumbnail',
  //     altText: 'What is Trinket course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Trinket Curriculum!',
  //     image: 'trinket-curriculum-video-thumbnail',
  //     altText: 'Trinket Curriculum course video thumbnail for kids',
  //   },
  // ],
  // PYGAME: [
  //   {
  //     title: 'What is Pygame?',
  //     image: 'what-is-pygame-video-thumbnail',
  //     altText: 'What is Pygame course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Pygame Curriculum!',
  //     image: 'pygame-curriculum-video-thumbnail',
  //     altText: 'Pygame Curriculum course video thumbnail for kids',
  //   },
  // ],
  // ANIMATION: [
  //   {
  //     title: 'What is Animation?',
  //     image: 'what-is-animation-video-thumbnail',
  //     altText: 'What is Animation course video thumbnail for kids',
  //   },
  //   {
  //     title: 'Animation Curriculum!',
  //     image: 'animation-curriculum-video-thumbnail',
  //     altText: 'Animation Curriculum course video thumbnail for kids',
  //   },
  // ],
}

export const COURSE_INCLUDES = {
  MIT_APP_INVENTOR: [
    {
      icon: 'integrated-development',
      title: 'Mobile App Development for Beginners',
      description:
        'Our MIT App Inventor lessons are perfect for beginners, introducing mobile app development through a simple drag-and-drop coding interface. Students will learn how to build interactive apps like a quiz app, health tracker, and doodling board, while mastering the basics of UI design, logic, and user interaction.',
    },
    {
      icon: 'mobile-app',
      title: 'Build Your First Apps',
      description:
        'In our MIT App Inventor courses, students dive into mobile app development by creating projects such as a health app, quiz app, and drawing pad. They explore components like buttons, web viewers, sensors, and layouts while gaining a solid understanding of how technology works and how to design user-friendly apps.',
    },
    {
      icon: 'introduction',
      title: 'Introduction to MIT App Inventor',
      description:
        'Our App Development Basics lessons introduce students to MIT App Inventor, guiding them through creating simple apps that they can use on their phones. Students learn to work with key components such as buttons, labels, web viewers, and sensors, while gaining essential skills in app design, logic, and interactivity.',
    },
    {
      icon: 'analytical-thinking',
      title: 'Creating Interactive Apps with MIT App Inventor',
      description:
        'In this course, students learn how to design fun and interactive apps such as a quiz app or health tracker. Using MIT App Inventor, they will explore how to integrate user input, buttons, labels, and sensors to create dynamic and functional apps. The focus is on mastering app development fundamentals while fostering creativity and problem-solving.',
    },
    {
      icon: 'health-app',
      title: 'Health App Development with MIT App Inventor',
      description:
        'Students will create a Health App using MIT App Inventor, where they will learn to track simple health metrics like steps or water intake. This project introduces students to the concept of sensors and user input, while helping them understand how to build apps that can collect and process real-world data.',
    },
  ],
  BOOTSTRAP: [
    {
      icon: 'responsive-website',
      title: 'Building Responsive Websites with Bootstrap',
      description:
        'As part of their learning journey, students will use Bootstrap, a popular framework, to build responsive websites that adapt to different screen sizes—whether on laptops, tablets, or smartphones. By incorporating Bootstrap into their projects, students learn to design modern web pages with ease, using pre-designed components like navigation bars, buttons, forms, and more.',
    },
    {
      icon: 'web-development',
      title: 'Web Development: Hands-On with Bootstrap',
      description:
        'Throughout our web development curriculum, students use Bootstrap as a tool to design and build interactive, responsive websites. They explore HTML, CSS, and JavaScript alongside Bootstrap, learning to create layouts, manage page structures with grid systems, and customize UI components to build beautiful and functional websites that work seamlessly across devices.',
    },
    {
      icon: 'programming',
      title: 'Integrating Bootstrap with Python Projects',
      description:
        'In our Python courses, students also use Bootstrap to create professional-looking front-end interfaces for their Flask or Django web applications. They learn how to combine Python’s back-end logic with Bootstrap’s responsive design to create full-stack web applications that are both functional and visually appealing.',
    },
    {
      icon: 'game-development',
      title: 'Game Development and Web Pages with Bootstrap',
      description:
        'For students exploring game development, Bootstrap plays a key role in designing web-based game pages. Students use it to build landing pages, leaderboards, and game result dashboards that complement their game development projects. By integrating Bootstrap with JavaScript, students create engaging, interactive web experiences that enhance their games.',
    },
    {
      icon: 'design-thinking',
      title: 'Creative Coding Projects with Bootstrap',
      description:
        'Students use Bootstrap in their creative coding projects to easily design and launch personal websites, portfolios, or interactive projects. By customizing Bootstrap components, students practice building functional websites while refining their coding skills, all within a professional development environment.',
    },
  ],
}

const Courses = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [coursePDFCode, setCoursePDFCode] = useState<string | null>('cp')
  const [gradeOptions, setGradeOptions] = useState<number[]>([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ])
  const handlePDFDownload = (courseCode: string, gradeOptions: any) => {
    setCoursePDFCode(courseCode)
    setIsModalOpen(true)
    setGradeOptions(gradeOptions)
  }

  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }

  getRandomHoverBg()

  return (
    <div className="bg-grey-100">
      <div
        className="container mx-auto flex flex-col space-y-10 leading-tight py-16 text-grey px-5"
        id="courses"
      >
        <CurriculumDownloadModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          grades={gradeOptions}
          curriculumPDFPrefix={coursePDFCode.toLowerCase()}
        />
        <div className="text-center">
          <h2 className="text-2xl lg:text-4xl font-600 mb-4">
            <Trans>
              Choose an{' '}
              <span className="underlined-text">AI & coding course</span> that
              excites your child
            </Trans>
          </h2>
        </div>

        <div className="flex flex-wrap justify-center">
          {Object.values(COURSE_INFO).map((course, i) => (
            <CourseCard
              key={i}
              course={course}
              onPDFDownload={handlePDFDownload}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Courses
